import type {
    ClientMarketplaceSettingEntity,
    ClientMarketplaceSettingUpdateEntity,
    ClientMarketplaceSettingsEntity,
    ClientMarketplaceSettingsUpdateEntity,
} from '@/entities/client-marketplace-settings.entity';
import type { ClientMarketplaceSettingsConstraintsEntity } from '@/entities/client-marketplace-settings-constraints.entity';
import type { ClientMarketplaceAvailabilityEntity } from '@/entities/client-marketplace-availability.entity';
import { Marketplace } from '@/enums/marketplace';
import { SUPPORTED_RUNTIME_MARKETPLACES, type RuntimeSupportedMarketplace } from '@/shared/marketplace';

export const DEFAULT_CLIENT_MARKETPLACE_REVIEW_IMPORT_LIMIT_MAX = 100000;

export const createDefaultClientMarketplaceSettingsConstraints = (): ClientMarketplaceSettingsConstraintsEntity => ({
    reviewImportLimitMax: DEFAULT_CLIENT_MARKETPLACE_REVIEW_IMPORT_LIMIT_MAX,
});

const toObjectOrNull = (value: unknown): Record<string, unknown> | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    return value as Record<string, unknown>;
};

const normalizeNullableNonNegativeInteger = (value: unknown): number | null =>
    typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;

const createDefaultClientMarketplaceSetting = (): ClientMarketplaceSettingEntity => ({
    enabled: false,
    preExistingReviewImportLimit: null,
});

export const createDefaultClientMarketplaceSettings = (): ClientMarketplaceSettingsEntity => ({
    [Marketplace.Wb]: createDefaultClientMarketplaceSetting(),
    [Marketplace.Ozon]: createDefaultClientMarketplaceSetting(),
});

export const normalizeClientMarketplaceSettingsEntity = (value: unknown): ClientMarketplaceSettingsEntity => {
    const defaults = createDefaultClientMarketplaceSettings();
    const source = toObjectOrNull(value);
    if (!source) {
        return defaults;
    }

    return SUPPORTED_RUNTIME_MARKETPLACES.reduce<ClientMarketplaceSettingsEntity>((settings, platform) => {
        const rawSetting = toObjectOrNull(source[platform]);
        settings[platform] = {
            enabled: typeof rawSetting?.enabled === 'boolean' ? rawSetting.enabled : defaults[platform].enabled,
            preExistingReviewImportLimit: normalizeNullableNonNegativeInteger(rawSetting?.preExistingReviewImportLimit),
        };
        return settings;
    }, createDefaultClientMarketplaceSettings());
};

export const normalizeClientMarketplaceSettingsConstraintsEntity = (
    value: unknown,
): ClientMarketplaceSettingsConstraintsEntity => {
    const defaults = createDefaultClientMarketplaceSettingsConstraints();
    const source = toObjectOrNull(value);
    if (!source) {
        return defaults;
    }

    return {
        reviewImportLimitMax:
            typeof source.reviewImportLimitMax === 'number' &&
            Number.isInteger(source.reviewImportLimitMax) &&
            source.reviewImportLimitMax >= 0
                ? source.reviewImportLimitMax
                : defaults.reviewImportLimitMax,
    };
};

export const marketplaceAvailabilityToClientMarketplaceSettings = (
    availability: Partial<ClientMarketplaceAvailabilityEntity>,
): ClientMarketplaceSettingsEntity => ({
    [Marketplace.Wb]: {
        enabled: availability.wb === true,
        preExistingReviewImportLimit: null,
    },
    [Marketplace.Ozon]: {
        enabled: availability.ozon === true,
        preExistingReviewImportLimit: null,
    },
});

export const clientMarketplaceSettingsToAvailability = (
    settings: ClientMarketplaceSettingsEntity,
): ClientMarketplaceAvailabilityEntity => ({
    wb: settings.wb.enabled,
    ozon: settings.ozon.enabled,
    yandexMarket: false,
});

export const clientMarketplaceSettingsToActivatedPlatforms = (
    settings: ClientMarketplaceSettingsEntity,
): RuntimeSupportedMarketplace[] => SUPPORTED_RUNTIME_MARKETPLACES.filter((platform) => settings[platform].enabled);

export const getClientMarketplaceSettingsUpdatePatch = (
    update: ClientMarketplaceSettingsUpdateEntity | undefined,
    platform: RuntimeSupportedMarketplace,
): ClientMarketplaceSettingUpdateEntity | undefined => {
    if (!update) {
        return undefined;
    }

    switch (platform) {
        case Marketplace.Wb:
            return update.wb;
        case Marketplace.Ozon:
            return update.ozon;
    }
};

export const mergeClientMarketplaceSettingsUpdate = (
    currentSettings: ClientMarketplaceSettingsEntity,
    update: ClientMarketplaceSettingsUpdateEntity,
): ClientMarketplaceSettingsEntity =>
    SUPPORTED_RUNTIME_MARKETPLACES.reduce<ClientMarketplaceSettingsEntity>((settings, platform) => {
        const patch = getClientMarketplaceSettingsUpdatePatch(update, platform);
        settings[platform] = {
            enabled: patch?.enabled ?? currentSettings[platform].enabled,
            preExistingReviewImportLimit:
                patch?.preExistingReviewImportLimit !== undefined
                    ? (patch.preExistingReviewImportLimit ?? null)
                    : currentSettings[platform].preExistingReviewImportLimit,
        };
        return settings;
    }, createDefaultClientMarketplaceSettings());

export const normalizeClientMarketplaceImportLimitInput = (value: unknown): number | null | undefined => {
    if (value === undefined) {
        return undefined;
    }

    if (value === null) {
        return null;
    }

    return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined;
};
